import { createTheme } from '@mui/material/styles';

// Verdana Health design system
export const newTheme = createTheme({
  palette: {
    primary: {
      main: '#0F172A',
      light: '#1E293B',
      dark: '#020617',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#64748B',
      light: '#94A3B8',
      dark: '#475569',
      contrastText: '#ffffff',
    },
    // Sage green for links, CTAs, highlights
    info: {
      main: '#059669',
      light: '#10B981',
      dark: '#047857',
      contrastText: '#ffffff',
    },
    success: {
      main: '#22C55E',
      light: '#4ADE80',
      dark: '#16A34A',
    },
    warning: {
      main: '#EAB308',
      light: '#FDE047',
      dark: '#CA8A04',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#64748B',
      disabled: '#94A3B8',
    },
    divider: '#E2E8F0',
  },
  typography: {
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '2rem', lineHeight: 1.2 },
    h2: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.25 },
    h3: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3 },
    h4: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 500, fontSize: '1rem', lineHeight: 1.35 },
    h5: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 500 },
    h6: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.4 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: '#F8FAFC' } },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.15s ease',
          '&:active': { transform: 'scale(0.98)' },
          '&.Mui-disabled': { opacity: 0.4 },
        },
        containedPrimary: {
          backgroundColor: '#0F172A',
          color: '#ffffff',
          boxShadow: '0 1px 3px rgba(15,23,42,0.03)',
          '&:hover': {
            backgroundColor: '#020617',
            boxShadow: '0 2px 6px rgba(15,23,42,0.05)',
          },
        },
        outlinedPrimary: {
          borderColor: '#0F172A',
          color: '#0F172A',
          borderWidth: 1,
          '&:hover': {
            backgroundColor: 'rgba(15,23,42,0.04)',
            borderWidth: 1,
          },
        },
        text: {
          color: '#475569',
          '&:hover': { backgroundColor: '#F1F5F9' },
        },
        sizeMedium: { padding: '10px 22px', fontSize: '0.875rem', minHeight: 42 },
        sizeLarge: { padding: '12px 28px', fontSize: '1rem', minHeight: 48 },
        sizeSmall: { padding: '6px 14px', fontSize: '0.875rem', minHeight: 32 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #E2E8F0',
          boxShadow: 'none',
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 6px rgba(15,23,42,0.05)',
        },
        elevation0: { boxShadow: 'none' },
        elevation1: { boxShadow: '0 1px 3px rgba(15,23,42,0.03)' },
        elevation2: { boxShadow: '0 2px 6px rgba(15,23,42,0.05)' },
        elevation4: { boxShadow: '0 4px 16px rgba(15,23,42,0.07)' },
        elevation8: { boxShadow: '0 8px 32px rgba(15,23,42,0.10)' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          height: 24,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          transition: 'box-shadow 0.15s ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0F172A' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0F172A',
            borderWidth: 2,
          },
          '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(15,23,42,0.09)' },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#EF4444',
            borderWidth: 2,
          },
          '&.Mui-error.Mui-focused': { boxShadow: '0 0 0 3px rgba(239,68,68,0.09)' },
          '&.Mui-disabled': {
            backgroundColor: '#F1F5F9',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          transition: 'all 0.15s ease',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderStyle: 'solid',
          borderColor: '#E2E8F0',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #E2E8F0',
          boxShadow: '0 1px 3px rgba(15,23,42,0.03)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #E2E8F0',
          boxShadow: 'none',
          borderRadius: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.15s ease',
          '&:active': { transform: 'scale(0.92)' },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { borderRadius: 6 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0F172A',
          color: '#F8FAFC',
          fontSize: '0.75rem',
          borderRadius: 8,
          padding: '6px 12px',
          maxWidth: 240,
        },
        arrow: { color: '#0F172A' },
      },
    },
  },
});

export const originalTheme = createTheme({
  palette: {
    background: {
      default: '#fafafb',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none' },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
});

export default newTheme;
